import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  createScrollIntentTracker,
  type ScrollIntent,
} from 'utils/scroll/scrollIntent';

const ScrollIntentContext = createContext<ScrollIntent | null>(null);

export default function ScrollIntentProvider({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [tracker] = useState(createScrollIntentTracker);

  const intent = useMemo(
    () =>
      tracker.track({
        key: location.key,
        navigationType,
        pathname: location.pathname,
        hash: location.hash,
        state: location.state,
      }),
    // A navigation is identified by its key; the rest belongs to it
    [tracker, location.key],
  );

  return (
    <ScrollIntentContext.Provider value={intent}>
      {children}
    </ScrollIntentContext.Provider>
  );
}

export const useScrollIntent = (): ScrollIntent => {
  const intent = useContext(ScrollIntentContext);
  if (!intent) {
    throw new Error('useScrollIntent must be used within ScrollIntentProvider');
  }
  return intent;
};
